import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import { get, post, upload } from './client';

/** Long enough for the first product of a tick to have finished. */
const TICK_SETTLE_MS = 30000;
import type {
  FreshnessOverview,
  RefillResult,
  AttemptBucket,
  PacingOverview,
  QueueFailure,
  QueueOverview,
  ImportResult,
  MaintenanceSummary,
  ResetResult,
  CheapestAvailabilityRow,
  CompetitorChurnRow,
  DeliveredRankRow,
  PromotedSlotStats,
  ReactionTimeRow,
  ShopRatingRow,
  CategoryStat,
  CompetitorStat,
  CostToWinRow,
  CrawlProgress,
  TickStarted,
  CrawlerStatus,
  DashboardSummary,
  FeedSyncRun,
  DataQuality,
  GapBucket,
  LogListParams,
  PagedLogs,
  PagedProducts,
  PositionBucket,
  PriceRecommendation,
  ProductDetail,
  ProductListParams,
  RankHistoryPoint,
  RepricingRow,
  TransportSpendPoint,
  VolatilityRow,
  ZenRowsRequestRow,
  ZenRowsUsageDay,
  ZenRowsUsageSummary,
  ZenRowsUsageTransport,
} from './types';

export function useSummary(): UseQueryResult<DashboardSummary> {
  return useQuery({ queryKey: ['summary'], queryFn: () => get<DashboardSummary>('/dashboard/summary') });
}

export function usePositionBuckets(): UseQueryResult<PositionBucket[]> {
  return useQuery({
    queryKey: ['positions'],
    queryFn: () => get<PositionBucket[]>('/dashboard/price-positions'),
  });
}

export function useGapBuckets(): UseQueryResult<GapBucket[]> {
  return useQuery({ queryKey: ['gaps'], queryFn: () => get<GapBucket[]>('/dashboard/price-gaps') });
}

export function useCategoryStats(limit = 12): UseQueryResult<CategoryStat[]> {
  return useQuery({
    queryKey: ['categories', limit],
    queryFn: () => get<CategoryStat[]>('/dashboard/categories', { limit }),
  });
}

export function useCompetitors(limit = 20): UseQueryResult<CompetitorStat[]> {
  return useQuery({
    queryKey: ['competitors', limit],
    queryFn: () => get<CompetitorStat[]>('/competitors', { limit }),
  });
}

export function useProducts(params: ProductListParams): UseQueryResult<PagedProducts> {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => get<PagedProducts>('/products', { ...params }),
  });
}

export function useProductDetail(id: number): UseQueryResult<ProductDetail> {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => get<ProductDetail>(`/products/${id}`),
    enabled: Number.isFinite(id),
  });
}

export function useRecommendations(limit = 30): UseQueryResult<PriceRecommendation[]> {
  return useQuery({
    queryKey: ['recommendations', limit],
    queryFn: () => get<PriceRecommendation[]>('/recommendations', { limit }),
  });
}

export function useCrawlerStatus(): UseQueryResult<CrawlerStatus> {
  return useQuery({
    queryKey: ['crawler'],
    queryFn: () => get<CrawlerStatus>('/crawler/status'),
    refetchInterval: 20000,
  });
}

export function useFeedRuns(limit = 10): UseQueryResult<FeedSyncRun[]> {
  return useQuery({ queryKey: ['feedRuns', limit], queryFn: () => get<FeedSyncRun[]>('/feed/runs', { limit }) });
}

export function useRescan(): UseMutationResult<CrawlerStatus, Error, number> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => post<CrawlerStatus>(`/crawler/products/${id}/rescan`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['crawler'] });
    },
  });
}

export function useCrawlTick(): UseMutationResult<TickStarted, Error, void> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => post<TickStarted>('/crawler/tick'),
    onSuccess: () => {
      /*
       * The tick has only just been accepted, so nothing has changed yet. The
       * queue is refreshed anyway to pick up the task moving to running, and
       * again a little later for the first result — beyond that the operator
       * watches the activity log.
       */
      void client.invalidateQueries({ queryKey: ['crawler'] });
      window.setTimeout(() => {
        void client.invalidateQueries({ queryKey: ['crawler'] });
        void client.invalidateQueries({ queryKey: ['summary'] });
      }, TICK_SETTLE_MS);
    },
  });
}

export function useFeedSync(): UseMutationResult<FeedSyncRun, Error, void> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => post<FeedSyncRun>('/feed/sync'),
    onSuccess: () => {
      void client.invalidateQueries();
    },
  });
}

export function useLogs(params: LogListParams, refetchMs: number | false): UseQueryResult<PagedLogs> {
  return useQuery({
    queryKey: ['logs', params],
    queryFn: () => get<PagedLogs>('/logs', { ...params }),
    refetchInterval: refetchMs,
    staleTime: 0,
  });
}

export function useCostToWin(limit = 40): UseQueryResult<CostToWinRow[]> {
  return useQuery({ queryKey: ['costToWin', limit], queryFn: () => get<CostToWinRow[]>('/insights/cost-to-win', { limit }) });
}

export function useRankHistory(days = 60): UseQueryResult<RankHistoryPoint[]> {
  return useQuery({ queryKey: ['rankHistory', days], queryFn: () => get<RankHistoryPoint[]>('/insights/rank-history', { days }) });
}

export function useVolatility(limit = 20): UseQueryResult<VolatilityRow[]> {
  return useQuery({ queryKey: ['volatility', limit], queryFn: () => get<VolatilityRow[]>('/insights/volatility', { limit }) });
}

export function useRepricing(limit = 20): UseQueryResult<RepricingRow[]> {
  return useQuery({ queryKey: ['repricing', limit], queryFn: () => get<RepricingRow[]>('/insights/repricing', { limit }) });
}

export function useDataQuality(): UseQueryResult<DataQuality> {
  return useQuery({ queryKey: ['dataQuality'], queryFn: () => get<DataQuality>('/insights/data-quality') });
}

export function useCrawlProgress(): UseQueryResult<CrawlProgress> {
  return useQuery({ queryKey: ['crawlProgress'], queryFn: () => get<CrawlProgress>('/insights/crawl-progress') });
}

export function useTransportSpend(days = 14): UseQueryResult<TransportSpendPoint[]> {
  return useQuery({ queryKey: ['transportSpend', days], queryFn: () => get<TransportSpendPoint[]>('/insights/transport-spend', { days }) });
}

export function useZenRowsSummary(): UseQueryResult<ZenRowsUsageSummary> {
  return useQuery({
    queryKey: ['zenrowsSummary'],
    queryFn: () => get<ZenRowsUsageSummary>('/zenrows/summary'),
    refetchInterval: 60000,
  });
}

export function useZenRowsDaily(days = 30): UseQueryResult<ZenRowsUsageDay[]> {
  return useQuery({ queryKey: ['zenrowsDaily', days], queryFn: () => get<ZenRowsUsageDay[]>('/zenrows/daily', { days }) });
}

export function useZenRowsByTransport(days = 30): UseQueryResult<ZenRowsUsageTransport[]> {
  return useQuery({
    queryKey: ['zenrowsTransport', days],
    queryFn: () => get<ZenRowsUsageTransport[]>('/zenrows/by-transport', { days }),
  });
}

export function useZenRowsRecent(limit = 40): UseQueryResult<ZenRowsRequestRow[]> {
  return useQuery({ queryKey: ['zenrowsRecent', limit], queryFn: () => get<ZenRowsRequestRow[]>('/zenrows/recent', { limit }) });
}

export function useDeliveredRank(limit = 20): UseQueryResult<DeliveredRankRow[]> {
  return useQuery({
    queryKey: ['deliveredRank', limit],
    queryFn: () => get<DeliveredRankRow[]>('/insights/delivered-rank', { limit }),
  });
}

export function useShopRatings(limit = 30): UseQueryResult<ShopRatingRow[]> {
  return useQuery({
    queryKey: ['shopRatings', limit],
    queryFn: () => get<ShopRatingRow[]>('/insights/shop-ratings', { limit }),
  });
}

export function useCheapestAvailability(): UseQueryResult<CheapestAvailabilityRow[]> {
  return useQuery({
    queryKey: ['cheapestAvailability'],
    queryFn: () => get<CheapestAvailabilityRow[]>('/insights/cheapest-availability'),
  });
}

export function usePromotedSlot(): UseQueryResult<PromotedSlotStats> {
  return useQuery({
    queryKey: ['promotedSlot'],
    queryFn: () => get<PromotedSlotStats>('/insights/promoted-slot'),
  });
}

export function useCompetitorChurn(days = 180, limit = 30): UseQueryResult<CompetitorChurnRow[]> {
  return useQuery({
    queryKey: ['competitorChurn', days, limit],
    queryFn: () => get<CompetitorChurnRow[]>('/insights/competitor-churn', { days, limit }),
  });
}

export function useReactionTime(days = 180, limit = 15): UseQueryResult<ReactionTimeRow[]> {
  return useQuery({
    queryKey: ['reactionTime', days, limit],
    queryFn: () => get<ReactionTimeRow[]>('/insights/reaction-time', { days, limit }),
  });
}

export function useMaintenanceSummary(): UseQueryResult<MaintenanceSummary> {
  return useQuery({
    queryKey: ['maintenanceSummary'],
    queryFn: () => get<MaintenanceSummary>('/maintenance/summary'),
  });
}

export function useResetFailedTasks() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => post<ResetResult>('/maintenance/reset-failed-tasks'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['maintenanceSummary'] });
      client.invalidateQueries({ queryKey: ['crawler'] });
    },
  });
}

export function useResetUnmatchedTasks() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => post<ResetResult>('/maintenance/reset-unmatched-tasks'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['maintenanceSummary'] });
      client.invalidateQueries({ queryKey: ['crawler'] });
      client.invalidateQueries({ queryKey: ['freshness'] });
    },
  });
}

export function useImportArchive() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => upload<ImportResult>('/maintenance/import', 'archive', file),
    // Everything on screen came from the database that was just replaced.
    onSuccess: () => client.invalidateQueries(),
  });
}

export function useQueueOverview(limit = 20): UseQueryResult<QueueOverview> {
  return useQuery({
    queryKey: ['queueOverview', limit],
    queryFn: () => get<QueueOverview>('/crawler/queue', { limit }),
    // The queue moves while you watch it.
    refetchInterval: 30_000,
  });
}

export function usePacing(): UseQueryResult<PacingOverview> {
  return useQuery({
    queryKey: ['pacing'],
    queryFn: () => get<PacingOverview>('/crawler/pacing'),
    refetchInterval: 60_000,
  });
}

export function useQueueFailures(limit = 20): UseQueryResult<QueueFailure[]> {
  return useQuery({
    queryKey: ['queueFailures', limit],
    queryFn: () => get<QueueFailure[]>('/crawler/failures', { limit }),
  });
}

export function useQueueAttempts(): UseQueryResult<AttemptBucket[]> {
  return useQuery({
    queryKey: ['queueAttempts'],
    queryFn: () => get<AttemptBucket[]>('/crawler/attempts'),
  });
}

export function useRefillQueue() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => post<RefillResult>('/crawler/refill'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['queueOverview'] });
      client.invalidateQueries({ queryKey: ['pacing'] });
      client.invalidateQueries({ queryKey: ['crawler'] });
      client.invalidateQueries({ queryKey: ['maintenanceSummary'] });
    },
  });
}

export function useFreshness(): UseQueryResult<FreshnessOverview> {
  return useQuery({
    queryKey: ['freshness'],
    queryFn: () => get<FreshnessOverview>('/insights/freshness'),
  });
}
